import type { LinkDialogOrigin } from "@shared/unified-tiptap/extensions/link/link-inline-extension"
import { useCallback, useMemo, useState } from "react"

/**
 * The add/edit link dialog's open state, shared between the editor's extensions (Mod-K opens
 * it, so `open` must exist before the editor is constructed) and the surfaces that render it.
 */
export interface LinkDialogController {
  /** The opener that started the dialog; null while it is closed. */
  origin: LinkDialogOrigin | null
  open: (origin: LinkDialogOrigin) => void
  close: () => void
}

export function useLinkDialog(): LinkDialogController {
  // The opener doubles as the open flag, so a submit can name the surface it came from.
  const [origin, setOrigin] = useState<LinkDialogOrigin | null>(null)
  // `current ?? next` keeps the opener that *started* the dialog: a second opener firing while
  // it is open would re-attribute the submit.
  const open = useCallback((next: LinkDialogOrigin) => setOrigin((current) => current ?? next), [])
  const close = useCallback(() => setOrigin(null), [])
  return useMemo(() => ({ origin, open, close }), [origin, open, close])
}
